import { useState, useEffect } from 'react';
import { Link } from 'react-router-dom';
import { useAuth } from '../../context/AuthContext';
import { toProfileFormData } from '../../services/api/users.service';
import DashboardLayout from '../../components/DashboardLayout';
import { Input } from '../../components/ui/input';
import { Label } from '../../components/ui/label';
import { Button } from '../../components/ui/button';
import { Badge } from '../../components/ui/badge';
import { toast } from 'sonner';
import {
  User, Mail, Phone, MapPin, Shield, CheckCircle2, Rocket,
  BadgeCheck, ArrowRight, Loader2, Sparkles, Compass, AlertCircle, Briefcase
} from 'lucide-react';

const getErrorMessage = (error, fallback) => {
  if (error?.details?.length) {
    return error.details.map((item) => item.message).join(' · ');
  }
  return error?.message || fallback;
};

export default function Profile() {
  const { user, loading, updateProfile, activatePro, setViewMode, isProView: isProViewFn } = useAuth();
  const [saving, setSaving] = useState(false);
  const [activating, setActivating] = useState(false);
  const [formData, setFormData] = useState({
    prenom: '',
    nom: '',
    telephone: '',
    ville: '',
  });

  useEffect(() => {
    if (user) {
      setFormData(toProfileFormData(user));
    }
  }, [user]);

  const isPro = user?.account_type === 'pro';
  const isProActive = isPro && user?.pro_status === 'actif';
  const isProSuspended = user?.proStatus === 'SUSPENDED';
  const isKycValidated = user?.kyc_status === 'valide';
  const isKycPending = user?.kyc_status === 'en_attente' || user?.kyc_status === 'en_attente_validation';
  const isProView = isProViewFn();

  const handleActivatePro = async () => {
    setActivating(true);
    try {
      await activatePro();
      toast.success('Votre espace Pro est activé. Bienvenue !');
    } catch (error) {
      toast.error(getErrorMessage(error, "Impossible d'activer l'espace Pro"));
    } finally {
      setActivating(false);
    }
  };

  const handleViewModeChange = async (mode) => {
    if (mode === 'pro') {
      if (isProView) return
      if (!isProActive) {
        await handleActivatePro()
        return
      }
      try {
        await setViewMode('pro')
      } catch (error) {
        toast.error(getErrorMessage(error, 'Impossible de passer en mode Professionnel'))
      }
      return
    }

    if (!isProView) return
    try {
      await setViewMode('particulier')
    } catch (error) {
      toast.error(getErrorMessage(error, 'Impossible de passer en mode Particulier'))
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault();

    const fullName = [formData.prenom.trim(), formData.nom.trim()].filter(Boolean).join(' ');
    if (fullName.length < 2) {
      toast.error('Le prénom et le nom doivent contenir au moins 2 caractères au total');
      return;
    }

    setSaving(true);
    try {
      await updateProfile(formData);
      toast.success('Profil mis à jour');
    } catch (error) {
      toast.error(getErrorMessage(error, 'Erreur lors de la mise à jour'));
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <DashboardLayout>
        <div className="w-full flex items-center justify-center py-24">
          <Loader2 className="w-8 h-8 animate-spin text-[hsl(var(--primary))]" aria-label="Chargement du profil" />
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout>
      <div className="w-full">
        <div className="mb-10 animate-fade-up">
          <p className="overline mb-4 flex items-center gap-3">
            <span className="w-8 h-px bg-[hsl(var(--primary))]" />
            Espace personnel
          </p>
          <h1 className="font-display text-4xl md:text-5xl text-foreground tracking-tight mb-3">
            Mon profil
          </h1>
          <p className="text-[hsl(var(--muted-foreground))] text-base max-w-xl">
            {isProView
              ? 'Gérez vos informations, votre espace professionnel et votre badge vérifié.'
              : 'Gérez vos informations personnelles et votre compte Click&Pro.'}
          </p>
        </div>

        {isProSuspended && (
          <div
            className="mb-8 rounded-sm border border-red-200 bg-red-50 p-6 flex items-start gap-3"
            data-testid="profile-pro-suspended-alert"
          >
            <AlertCircle className="w-5 h-5 text-red-600 shrink-0 mt-0.5" />
            <div>
              <p className="font-semibold text-red-900">Espace Pro suspendu</p>
              <p className="text-sm text-red-700 mt-1">
                Votre accès professionnel a été suspendu. Contactez le support Click&amp;Pro pour plus d&apos;informations.
              </p>
            </div>
          </div>
        )}

        {!isProActive && !isProSuspended ? (
          <div
            className="mb-8 relative overflow-hidden rounded-sm border border-[hsl(var(--border))] bg-[hsl(var(--obsidian))] text-white p-8 md:p-10 grain-overlay animate-fade-up"
            data-testid="profile-pro-activation-card"
          >
            <div className="relative grid md:grid-cols-[1fr_auto] items-center gap-8">
              <div className="max-w-2xl">
                <p className="overline mb-4 text-[hsl(var(--accent))] flex items-center gap-3">
                  <Sparkles className="w-3.5 h-3.5" /> Gratuit · Activation immédiate
                </p>
                <h2 className="font-serif-display text-3xl md:text-4xl leading-tight mb-3">
                  Activez votre <em className="text-[hsl(var(--accent))] not-italic">espace Pro</em> en un clic.
                </h2>
                <p className="text-white/75 leading-relaxed text-[15px]">
                  Click&amp;Pro est <strong className="text-white">gratuit</strong>. Aucun abonnement,
                  aucun paiement requis. Activez votre espace pour publier vos services,
                  répondre aux demandes, publier des offres d&apos;emploi et créer votre One Page Pro.
                </p>
                <div className="flex flex-wrap gap-4 mt-6 text-sm text-white/70">
                  <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))]" /> Sans abonnement</span>
                  <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))]" /> Sans paiement</span>
                  <span className="flex items-center gap-2"><CheckCircle2 className="w-4 h-4 text-[hsl(var(--accent))]" /> Sans validation préalable</span>
                </div>
              </div>
              <Button
                onClick={handleActivatePro}
                disabled={activating}
                className="w-full md:w-auto bg-white text-[hsl(var(--obsidian))] hover:bg-white/90 h-14 px-8 rounded-sm font-semibold tracking-wide"
                data-testid="profile-activate-pro-btn"
              >
                {activating ? (
                  <><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Activation…</>
                ) : (
                  <><Rocket className="w-4 h-4 mr-2" /> Activer mon espace Pro</>
                )}
              </Button>
            </div>
          </div>
        ) : isProActive && isProView ? (
          <div
            className="mb-8 lux-dashboard-card-sm p-8 animate-fade-up"
            data-testid="profile-pro-active-card"
          >
            <div className="flex flex-col md:flex-row md:items-center gap-6 md:gap-10">
              <div className="flex items-center gap-4">
                <div className="w-12 h-12 rounded-sm bg-[hsl(var(--success))]/10 flex items-center justify-center">
                  <CheckCircle2 className="w-6 h-6 text-[hsl(var(--success))]" />
                </div>
                <div>
                  <p className="overline mb-1">Espace professionnel</p>
                  <h2 className="font-serif-display text-2xl text-[hsl(var(--foreground))]">
                    Espace Pro <em className="text-[hsl(var(--primary))] not-italic">actif</em>
                  </h2>
                  <p className="text-sm text-[hsl(var(--muted-foreground))] mt-1">
                    Mode actuel : {isProView ? 'Professionnel' : 'Particulier'}
                  </p>
                </div>
              </div>

              <div className="flex flex-col md:flex-row md:items-center gap-3 md:ml-auto">
                {isKycValidated ? (
                  <Badge className="badge-clickpro-verified" data-testid="profile-verified-badge">
                    <BadgeCheck className="w-3.5 h-3.5" /> Professionnel vérifié
                  </Badge>
                ) : isKycPending ? (
                  <Badge className="bg-[hsl(var(--warning))]/20 text-[hsl(var(--primary-hover))] border-0 text-[11px] uppercase tracking-wide">
                    Badge vérifié — en revue
                  </Badge>
                ) : null}

                <Link to="/tableau-de-bord/ma-page-pro">
                  <Button variant="outline" className="rounded-sm" data-testid="profile-edit-page-btn">
                    <Compass className="w-4 h-4 mr-2" /> Ma One Page
                  </Button>
                </Link>
              </div>
            </div>
          </div>
        ) : null}

        {isProView && isProActive && !isKycValidated && (
          <div className="mb-8 lux-dashboard-card-sm p-8 animate-fade-up" data-testid="profile-verified-badge-card">
            <div className="grid md:grid-cols-[1fr_auto] gap-6 items-center">
              <div className="max-w-2xl">
                <p className="overline mb-3 flex items-center gap-2">
                  <BadgeCheck className="w-3.5 h-3.5 text-[hsl(var(--primary))]" />
                  Badge professionnel vérifié
                </p>
                <h3 className="font-serif-display text-2xl text-[hsl(var(--foreground))] mb-2">
                  Gagnez la confiance des clients.
                </h3>
                <p className="text-[hsl(var(--muted-foreground))] leading-relaxed text-[15px]">
                  Soumettez vos justificatifs (KBIS, assurance, identité). Notre équipe vérifie
                  votre dossier sous 48&nbsp;à&nbsp;72&nbsp;h. Une fois validé, un badge{' '}
                  <em className="text-[hsl(var(--primary))] not-italic">«&nbsp;Professionnel vérifié&nbsp;»</em>{' '}
                  est affiché sur votre fiche publique.
                </p>
              </div>
              <Link to="/tableau-de-bord/kyc" className="w-full md:w-auto">
                <Button className="btn-clickpro-outline w-full md:w-auto" data-testid="profile-request-badge-btn">
                  {isKycPending ? 'Suivre ma demande' : 'Demander le badge'}
                  <ArrowRight className="w-4 h-4 ml-2" />
                </Button>
              </Link>
            </div>
          </div>
        )}

        <div className="grid lg:grid-cols-3 gap-6">
          <div className="lg:col-span-2">
            <div className="lux-dashboard-card-sm p-8">
              <h3 className="font-serif-display text-2xl text-[hsl(var(--foreground))] mb-1">Informations</h3>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-8">Vos données affichées et utilisées sur la plateforme.</p>

              <form onSubmit={handleSubmit} className="space-y-5" data-testid="profile-form">
                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="prenom" className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Prénom</Label>
                    <Input
                      id="prenom"
                      value={formData.prenom}
                      onChange={(e) => setFormData({ ...formData, prenom: e.target.value })}
                      className="input-clickpro mt-2"
                      required
                      autoComplete="given-name"
                      data-testid="profile-prenom-input"
                    />
                  </div>
                  <div>
                    <Label htmlFor="nom" className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Nom</Label>
                    <Input
                      id="nom"
                      value={formData.nom}
                      onChange={(e) => setFormData({ ...formData, nom: e.target.value })}
                      className="input-clickpro mt-2"
                      required
                      autoComplete="family-name"
                      data-testid="profile-nom-input"
                    />
                  </div>
                </div>

                <div>
                  <Label htmlFor="email" className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Email</Label>
                  <div className="relative mt-2">
                    <Mail className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                    <Input id="email" value={user?.email || ''} disabled className="input-clickpro pl-10 opacity-60" />
                  </div>
                  <p className="text-xs text-[hsl(var(--muted-foreground))] mt-1.5">L&apos;email ne peut pas être modifié.</p>
                </div>

                <div className="grid md:grid-cols-2 gap-4">
                  <div>
                    <Label htmlFor="telephone" className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Téléphone</Label>
                    <div className="relative mt-2">
                      <Phone className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="telephone"
                        type="tel"
                        value={formData.telephone}
                        onChange={(e) => setFormData({ ...formData, telephone: e.target.value })}
                        className="input-clickpro pl-10"
                        autoComplete="tel"
                        data-testid="profile-telephone-input"
                      />
                    </div>
                  </div>
                  <div>
                    <Label htmlFor="ville" className="text-xs uppercase tracking-wider text-[hsl(var(--muted-foreground))]">Ville</Label>
                    <div className="relative mt-2">
                      <MapPin className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-[hsl(var(--muted-foreground))]" />
                      <Input
                        id="ville"
                        value={formData.ville}
                        onChange={(e) => setFormData({ ...formData, ville: e.target.value })}
                        className="input-clickpro pl-10"
                        autoComplete="address-level2"
                        data-testid="profile-ville-input"
                      />
                    </div>
                  </div>
                </div>

                <Button type="submit" disabled={saving} className="btn-clickpro-primary" data-testid="profile-save-btn">
                  {saving ? (<><Loader2 className="w-4 h-4 mr-2 animate-spin" /> Mise à jour…</>) : 'Enregistrer'}
                </Button>
              </form>
            </div>
          </div>

          <aside className="space-y-6">
            <div
              className="lux-dashboard-card-sm p-6"
              data-testid="profile-view-mode-switch"
            >
              <p className="overline mb-1">Mode d&apos;affichage</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] mb-5 leading-relaxed">
                Choisissez l&apos;espace que vous consultez sur Click&amp;Pro.
              </p>

              {activating ? (
                <div className="flex items-center justify-center py-6" aria-live="polite">
                  <Loader2 className="w-5 h-5 animate-spin text-[hsl(var(--primary))]" aria-label="Activation en cours" />
                </div>
              ) : (
                <div
                  className="grid grid-cols-2 gap-2 p-1 bg-[hsl(var(--surface-subtle))] rounded-sm border border-[hsl(var(--border))]"
                  role="group"
                  aria-label="Basculer entre mode particulier et mode professionnel"
                >
                  <button
                    type="button"
                    onClick={() => handleViewModeChange('particulier')}
                    disabled={isProSuspended}
                    aria-pressed={!isProView}
                    data-testid="profile-mode-particulier"
                    className={`flex flex-col items-center gap-2 py-3.5 px-2 rounded-sm text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] ${
                      !isProView
                        ? 'bg-white shadow-sm text-[hsl(var(--foreground))] ring-1 ring-[hsl(var(--border))]'
                        : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
                    } ${isProSuspended ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <User className="w-4 h-4" aria-hidden="true" />
                    Particulier
                  </button>
                  <button
                    type="button"
                    onClick={() => handleViewModeChange('pro')}
                    disabled={isProSuspended}
                    aria-pressed={isProView}
                    data-testid="profile-pro-mode-switch"
                    className={`flex flex-col items-center gap-2 py-3.5 px-2 rounded-sm text-sm font-medium transition-all focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--primary))] ${
                      isProView
                        ? 'bg-white shadow-sm text-[hsl(var(--primary))] ring-1 ring-[hsl(var(--primary)/0.3)]'
                        : 'text-[hsl(var(--muted-foreground))] hover:text-[hsl(var(--foreground))]'
                    } ${isProSuspended ? 'opacity-50 cursor-not-allowed' : ''}`}
                  >
                    <Briefcase className="w-4 h-4" aria-hidden="true" />
                    Pro
                  </button>
                </div>
              )}

              <p className="text-xs text-[hsl(var(--muted-foreground))] mt-4 leading-relaxed">
                {isProView
                  ? 'Vous consultez vos outils professionnels : fiche artisan, réponses aux demandes, offres d\u2019emploi…'
                  : 'Vous consultez vos services client : publier une demande, trouver un artisan…'}
              </p>
            </div>

            <div className="lux-dashboard-card-sm p-6">
              <p className="overline mb-4">Type de compte</p>
              <div className="flex items-center gap-3 mb-5">
                <div className="w-10 h-10 rounded-sm bg-[hsl(var(--primary)/0.08)] flex items-center justify-center text-[hsl(var(--primary))]">
                  <User className="w-5 h-5" />
                </div>
                <div>
                  <p className="font-semibold text-[hsl(var(--foreground))] capitalize">
                    {isProView ? 'Professionnel' : 'Particulier'}
                  </p>
                  <p className="text-xs text-[hsl(var(--muted-foreground))]">
                    {isProView ? 'Mode professionnel' : 'Mode particulier'}
                  </p>
                </div>
              </div>

              <div className="space-y-3 pt-4 border-t border-[hsl(var(--border))]">
                {isProView && (
                  <>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-[hsl(var(--muted-foreground))]">Espace Pro</span>
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isProActive ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--muted-foreground))]'}`}>
                        {isProActive ? 'Actif' : isProSuspended ? 'Suspendu' : 'Non activé'}
                      </span>
                    </div>
                    <div className="flex justify-between items-center text-sm">
                      <span className="text-[hsl(var(--muted-foreground))]">Badge vérifié</span>
                      <span className={`text-xs font-semibold uppercase tracking-wide ${isKycValidated ? 'text-[hsl(var(--primary))]' : 'text-[hsl(var(--muted-foreground))]'}`}>
                        {isKycValidated ? <><BadgeCheck className="w-3 h-3 inline mr-1" /> Vérifié</> : isKycPending ? 'En revue' : 'Non demandé'}
                      </span>
                    </div>
                  </>
                )}
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">Profil complété</span>
                  <span className={`text-xs font-semibold uppercase tracking-wide ${user?.profile_completed ? 'text-[hsl(var(--success))]' : 'text-[hsl(var(--muted-foreground))]'}`}>
                    {user?.profile_completed ? 'Oui' : 'À compléter'}
                  </span>
                </div>
                <div className="flex justify-between items-center text-sm">
                  <span className="text-[hsl(var(--muted-foreground))]">Statut</span>
                  <span className="text-xs font-semibold uppercase tracking-wide text-[hsl(var(--foreground))]">
                    {user?.status === 'actif' ? 'Actif' : user?.status || '—'}
                  </span>
                </div>
              </div>
            </div>

            <div className="rounded-sm border border-[hsl(var(--border))] bg-[hsl(var(--surface-subtle))] p-6">
              <p className="overline mb-3 flex items-center gap-2"><Shield className="w-3.5 h-3.5" /> Confidentialité</p>
              <p className="text-sm text-[hsl(var(--muted-foreground))] leading-relaxed">
                Click&amp;Pro ne partage jamais vos informations sans votre accord. Aucune donnée bancaire n&apos;est stockée sur la plateforme.
              </p>
            </div>
          </aside>
        </div>
      </div>
    </DashboardLayout>
  );
}
